import { formatToFractional } from "../controls/conversions";
import { SCALE } from "../constants";
import { DIMENTION_FONT_SIZE } from "../constants";

/**
 * Draws SVG text with a background rectangle.
 *
 * @param {d3.Selection} svg - The D3 SVG container to append to.
 * @param {number} x - The x position of the text.
 * @param {number} y - The y position of the text.
 * @param {string} anchor - Text anchor (e.g., 'start', 'middle', 'end').
 * @param {string} baseline - Dominant baseline (e.g., 'middle', 'hanging').
 * @param {number} fontSize - Font size in pixels.
 * @param {string} color - Text color (e.g., 'black').
 * @param {string} textDirection - The vertical or horizontal text.
 * @param {string} textContent - The text to render.

 * @param {string} [bgColor='white'] - Background color behind text.
 * @param {number} [padding=2] - Padding around text inside the background.
 */

function drawTextWithBg(
  svg,
  x,
  y,
  anchor,
  baseline,
  fontSize,
  color,
  textContent,
  textDirection = 'horizontal', // ⬅️ NEW PARAM
  bgColor = 'white',
  padding = 2
) {
  // Create group to contain rect + text
  const group = svg.append('g');

  // Optional rotation
  if (textDirection === 'vertical') {
    group.attr('transform', `rotate(90, ${x}, ${y})`);
    anchor = 'middle';
    y = y-fontSize/2;
  }

  // Append text
  const text = group.append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('text-anchor', anchor)
    .attr('dominant-baseline', baseline)
    .attr('font-size', fontSize)
    .attr('fill', color)
    .text(textContent);

  // Compute bounding box AFTER text is rendered
  const bbox = text.node().getBBox();

  // Insert background rect behind text
  group.insert('rect', 'text')
    .attr('x', bbox.x - padding)
    .attr('y', bbox.y - padding)
    .attr('width', bbox.width + 2 * padding)
    .attr('height', bbox.height + 2 * padding)
    .attr('fill', bgColor);

  // Ensure label group is on top
  group.raise();
}


export function calculatePanelDimensions(nSections, doorData, frameWidth = 10, gap = 2) {
    let w_pure = 0;
  
    for (let i = 1; i <= nSections; i++) {
      const key = `windowWidth${i}_d`;
      const value = parseFloat(doorData?.frameData?.[key] || 0);
      w_pure += value;
    }
  
    let uHeight = parseFloat(doorData?.frameData?.uHeight_d || 0);
    if (doorData?.frameData?.topRowConf === 'Arch111'){
      if (parseFloat(doorData?.frameData?.archLegHeight_d || 0) > 0){
        uHeight = uHeight + parseFloat(doorData?.frameData?.archLegHeight_d || 0) - 4*frameWidth;
      }
    }
    const bHeight = parseFloat(doorData?.frameData?.windowHeight_d || 0);
  
    const totalHeight = uHeight + bHeight;
    const h = totalHeight + 2 * frameWidth;
  
    //const w = w_pure + nSections * 2 * frameWidth + nSections *  gap;

    const w = w_pure + (1*nSections + 1) + nSections * 2 * frameWidth + (nSections - 1) * gap;
    const w_label = formatToFractional(w_pure/SCALE);
    const h_label = formatToFractional(totalHeight/SCALE);
  
    return { w_pure, w, h, w_label, h_label, uHeight };
  }
export function calculatePanelDimensions_real(nSections, doorData, frameWidth = 10, gap = 2) {
    let w_pure = 0;

    for (let i = 1; i <= nSections; i++) {
      const key = `windowWidth${i}_d_real`;
      const value = parseFloat(doorData?.frameData?.[key] || 0);
      w_pure += value;
    }

    let uHeight = parseFloat(doorData?.frameData?.uHeight_d_real || 0);
    if (doorData?.frameData?.topRowConf === 'Arch111'){
      if (parseFloat(doorData?.frameData?.archLegHeight_d_real || 0) > 0){
        uHeight = uHeight + parseFloat(doorData?.frameData?.archLegHeight_d_real || 0) - 4*frameWidth;
      }
    }
    const bHeight = parseFloat(doorData?.frameData?.windowHeight_d_real || 0);

    const totalHeight = uHeight + bHeight;
    const h = totalHeight + 2 * frameWidth;

    //const w = w_pure + nSections * 2 * frameWidth + nSections *  gap;

    const w = w_pure + (1*nSections + 1) + nSections * 2 * frameWidth + (nSections - 1) * gap;
    const w_label = formatToFractional(w_pure);
    const h_label = formatToFractional(totalHeight);
    const uHeight_label = formatToFractional(uHeight);

    return { w_pure, w, h, w_label, h_label, uHeight, uHeight_label };
  }

export function displaySizes(
    svg,
    initX = 0,
    initY = 0,
    w,
    h,
    w_label = 'w',
    h_label = 'h',
    upperHeight = 0,
    uHeight_label = '',
    frameWidth = 10,
    gap = 2
) {
    const textOffset = 15;
    const tickSize = 8;
    const fontSize = DIMENTION_FONT_SIZE;

    // Offsets and adjustments
    const hLineX = initX - frameWidth;                // ← Move horizontal line to the left
    let hLineY = initY + h + frameWidth;            // ↓ Move horizontal line down

    const vLineX = initX + w + frameWidth;            // → Move vertical lines to the right
    const vLineY = initY - frameWidth;                // ↑ Move vertical lines to the top

    let totalOnly_X = frameWidth;
    let vTotalHeight = h + 2 * frameWidth;          // ↑↓ Extend vertical line height
    if (upperHeight === 0){
      hLineY = hLineY - 2*frameWidth;
    }

    const offsetTick_X = 3.5;
    const offsetTick_Y = 3.5;


    // === Horizontal Size Indicator ===
    // Left tick
    svg.append('line')
        .attr('x1', hLineX)
        .attr('y1', hLineY - tickSize / 2 + 2*frameWidth - offsetTick_Y)
        .attr('x2', hLineX)
        .attr('y2', hLineY + tickSize / 2 + 2*frameWidth - offsetTick_Y)
        .attr('stroke', 'black')
        .attr('stroke-width', 2);

    // Right tick
    svg.append('line')
        .attr('x1', hLineX + w)
        .attr('y1', hLineY - tickSize / 2 + 2*frameWidth - offsetTick_Y)
        .attr('x2', hLineX + w)
        .attr('y2', hLineY + tickSize / 2 + 2*frameWidth - offsetTick_Y)
        .attr('stroke', 'black')
        .attr('stroke-width', 2);

    // Horizontal line
    svg.append('line')
        .attr('x1', hLineX )
        .attr('y1', hLineY + 2*frameWidth)
        .attr('x2', hLineX + w)
        .attr('y2', hLineY + 2*frameWidth)
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);

    // Horizontal label
    drawTextWithBg(
      svg,
      hLineX + w / 2,
      hLineY + textOffset + 0.5*frameWidth,
      'middle',
      'middle',
      fontSize,
      'black',
      w_label
    );
    if (0){
    svg.append('text')
        .attr('x', hLineX + w / 2)
        .attr('y', hLineY + textOffset + 2*frameWidth)
        .attr('text-anchor', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', 'black')
        .text(w_label);
    }
    // === Short Vertical (Upper Section)
    let upperY2 = 0;
    let upperHeight_imperial = uHeight_label || formatToFractional(upperHeight);
    if (upperHeight > 0) {
        // Use the passed uHeight_label instead of recalculating from scaled upperHeight
        upperY2 = vLineY + upperHeight + 2 * frameWidth;

        // Vertical line
        svg.append('line')
            .attr('x1', vLineX)
            .attr('y1', vLineY)
            .attr('x2', vLineX)
            .attr('y2', upperY2)
            .attr('stroke', 'black')
            .attr('stroke-width', 0.5);

        // Top tick
        svg.append('line')
            .attr('x1', vLineX - tickSize / 2 - offsetTick_X)
            .attr('y1', vLineY)
            .attr('x2', vLineX + tickSize / 2 - offsetTick_X)
            .attr('y2', vLineY)
            .attr('stroke', 'black')
            .attr('stroke-width', 2);

        // Bottom tick
        svg.append('line')
            .attr('x1', vLineX - tickSize / 2 - offsetTick_X)
            .attr('y1', upperY2)
            .attr('x2', vLineX + tickSize / 2 - offsetTick_X)
            .attr('y2', upperY2)
            .attr('stroke', 'black')
            .attr('stroke-width', 2);

        // Label
        if (0){
        svg.append('text')
            .attr('x', vLineX + textOffset - 1.5*textOffset)
            .attr('y', vLineY + (upperY2 - vLineY) / 2)
            .attr('text-anchor', 'start')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', fontSize)
            .attr('fill', 'black')
            .text(`${upperHeight_imperial}`);
        }
    }
    if (upperHeight === 0){
      vTotalHeight = vTotalHeight - 2* frameWidth;
    }
    // === Full Vertical Line (Total Height)
    if (upperHeight === 0){
      totalOnly_X = 3*frameWidth;
    }
    const k = 1.5;
    svg.append('line')
        .attr('x1', vLineX + k*textOffset - totalOnly_X)
        .attr('y1', vLineY)
        .attr('x2', vLineX + k*textOffset - totalOnly_X)
        .attr('y2', vLineY + vTotalHeight)
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);


    // Top tick
    svg.append('line')
        .attr('x1', vLineX - tickSize / 2 + k*textOffset - offsetTick_X - totalOnly_X)
        .attr('y1', vLineY)
        .attr('x2', vLineX + tickSize / 2 + k*textOffset - offsetTick_X - totalOnly_X)
        .attr('y2', vLineY)
        .attr('stroke', 'black')
        .attr('stroke-width', 2);

    // Bottom tick
    svg.append('line')
        .attr('x1', vLineX - tickSize / 2 + k*textOffset - offsetTick_X - totalOnly_X)
        .attr('y1', vLineY + vTotalHeight)
        .attr('x2', vLineX + tickSize / 2 + k*textOffset - offsetTick_X - totalOnly_X)
        .attr('y2', vLineY + vTotalHeight)
        .attr('stroke', 'black')
        .attr('stroke-width', 2);

    // make vertical total Label
    drawTextWithBg(
        svg,
        vLineX + textOffset - totalOnly_X,      // x
        vLineY + vTotalHeight / 2,              // y
        'start',                                // text-anchor
        'middle',                               // dominant-baseline
        fontSize,                                     // font-size
        'black',                                // text color
        h_label,                                // text content
        'vertical'
    );
    if (0){
    svg.append('text')
        .attr('x', vLineX + textOffset)
        .attr('y', vLineY + vTotalHeight / 2)
        .attr('text-anchor', 'start')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', 'black')
        .text(h_label);
    }

    if (upperHeight > 0 && upperHeight_imperial && upperHeight_imperial !== "0\""){
      //make vertical upper label
      drawTextWithBg(
        svg,
        vLineX + textOffset - 1.5 * textOffset, // x
        vLineY + (upperY2 - vLineY) / 2,        // y
        'start',                                // text-anchor
        'middle',                               // dominant-baseline
        fontSize,                                     // font-size
        'black',                                // text color
        `${upperHeight_imperial}`,              // text content
        'vertical'
      );
    }
} 

export function displaySizes_old(
    svg,
    initX = 0,
    initY = 0,
    w,
    h,
    w_label = 'w',
    h_label = 'h',
    upperHeight = 0,
    frameWidth = 10,
    gap = 2
) {
    const textOffset = 15;
    const tickSize = 8;
    const fontSize = DIMENTION_FONT_SIZE;

    // Offsets and adjustments
    const hLineX = initX - frameWidth;                // ← Move horizontal line to the left
    let hLineY = initY + h + frameWidth;            // ↓ Move horizontal line down

    const vLineX = initX + w + frameWidth;            // → Move vertical lines to the right
    const vLineY = initY - frameWidth;                // ↑ Move vertical lines to the top

    let totalOnly_X = frameWidth;
    let vTotalHeight = h + 2 * frameWidth;          // ↑↓ Extend vertical line height
    if (upperHeight === 0){
      hLineY = hLineY - 2*frameWidth;
    }

    const offsetTick_X = 3.5;
    const offsetTick_Y = 3.5;


    // === Horizontal Size Indicator ===
    // Left tick
    svg.append('line')
        .attr('x1', hLineX)
        .attr('y1', hLineY - tickSize / 2 + 2*frameWidth - offsetTick_Y)
        .attr('x2', hLineX)
        .attr('y2', hLineY + tickSize / 2 + 2*frameWidth - offsetTick_Y)
        .attr('stroke', 'black')
        .attr('stroke-width', 2);

    // Right tick
    svg.append('line')
        .attr('x1', hLineX + w)
        .attr('y1', hLineY - tickSize / 2 + 2*frameWidth - offsetTick_Y)
        .attr('x2', hLineX + w)
        .attr('y2', hLineY + tickSize / 2 + 2*frameWidth - offsetTick_Y)
        .attr('stroke', 'black')
        .attr('stroke-width', 2);

    // Horizontal line
    svg.append('line')
        .attr('x1', hLineX )
        .attr('y1', hLineY + 2*frameWidth)
        .attr('x2', hLineX + w)
        .attr('y2', hLineY + 2*frameWidth)
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);

    // Horizontal label
    drawTextWithBg(
      svg,
      hLineX + w / 2,
      hLineY + textOffset + 0.5*frameWidth,
      'middle',
      'middle',
      fontSize,
      'black',
      w_label
    );
    if (0){
    svg.append('text')
        .attr('x', hLineX + w / 2)
        .attr('y', hLineY + textOffset + 2*frameWidth)
        .attr('text-anchor', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', 'black')
        .text(w_label);
    }
    // === Short Vertical (Upper Section)
    let upperY2 = 0;
    let upperHeight_imperial = 0;
    if (upperHeight > 0) {
        upperHeight_imperial = formatToFractional(upperHeight/SCALE);
        upperY2 = vLineY + upperHeight + 2 * frameWidth;

        // Vertical line
        svg.append('line')
            .attr('x1', vLineX)
            .attr('y1', vLineY)
            .attr('x2', vLineX)
            .attr('y2', upperY2)
            .attr('stroke', 'black')
            .attr('stroke-width', 0.5);

        // Top tick
        svg.append('line')
            .attr('x1', vLineX - tickSize / 2 - offsetTick_X)
            .attr('y1', vLineY)
            .attr('x2', vLineX + tickSize / 2 - offsetTick_X)
            .attr('y2', vLineY)
            .attr('stroke', 'black')
            .attr('stroke-width', 2);

        // Bottom tick
        svg.append('line')
            .attr('x1', vLineX - tickSize / 2 - offsetTick_X)
            .attr('y1', upperY2)
            .attr('x2', vLineX + tickSize / 2 - offsetTick_X)
            .attr('y2', upperY2)
            .attr('stroke', 'black')
            .attr('stroke-width', 2);

        // Label
        if (0){
        svg.append('text')
            .attr('x', vLineX + textOffset - 1.5*textOffset)
            .attr('y', vLineY + (upperY2 - vLineY) / 2)
            .attr('text-anchor', 'start')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', fontSize)
            .attr('fill', 'black')
            .text(`${upperHeight_imperial}`);
        }
    }
    if (upperHeight === 0){
      vTotalHeight = vTotalHeight - 2* frameWidth;
    }
    // === Full Vertical Line (Total Height)
    if (upperHeight === 0){
      totalOnly_X = 3*frameWidth;
    }
    const k = 1.5;
    svg.append('line')
        .attr('x1', vLineX + k*textOffset - totalOnly_X)
        .attr('y1', vLineY)
        .attr('x2', vLineX + k*textOffset - totalOnly_X)
        .attr('y2', vLineY + vTotalHeight)
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);


    // Top tick
    svg.append('line')
        .attr('x1', vLineX - tickSize / 2 + k*textOffset - offsetTick_X - totalOnly_X)
        .attr('y1', vLineY)
        .attr('x2', vLineX + tickSize / 2 + k*textOffset - offsetTick_X - totalOnly_X)
        .attr('y2', vLineY)
        .attr('stroke', 'black')
        .attr('stroke-width', 2);

    // Bottom tick
    svg.append('line')
        .attr('x1', vLineX - tickSize / 2 + k*textOffset - offsetTick_X - totalOnly_X)
        .attr('y1', vLineY + vTotalHeight)
        .attr('x2', vLineX + tickSize / 2 + k*textOffset - offsetTick_X - totalOnly_X)
        .attr('y2', vLineY + vTotalHeight)
        .attr('stroke', 'black')
        .attr('stroke-width', 2);

    // make vertical total Label
    drawTextWithBg(
        svg,
        vLineX + textOffset - totalOnly_X,      // x
        vLineY + vTotalHeight / 2,              // y
        'start',                                // text-anchor
        'middle',                               // dominant-baseline
        fontSize,                                     // font-size
        'black',                                // text color
        h_label,                                // text content
        'vertical'
    );
    if (0){
    svg.append('text')
        .attr('x', vLineX + textOffset)
        .attr('y', vLineY + vTotalHeight / 2)
        .attr('text-anchor', 'start')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', 'black')
        .text(h_label);
    }

    if (upperHeight > 0 && upperHeight_imperial && upperHeight_imperial !== "0\""){
      //make vertical upper label
      drawTextWithBg(
        svg,
        vLineX + textOffset - 1.5 * textOffset, // x
        vLineY + (upperY2 - vLineY) / 2,        // y
        'start',                                // text-anchor
        'middle',                               // dominant-baseline
        fontSize,                                     // font-size
        'black',                                // text color
        `${upperHeight_imperial}`,              // text content
        'vertical'
      );
    }
} 
  
    